import { useState, useEffect, useCallback } from "react";
import { useHistory } from "react-router-dom";
import styles from "./Cartpage.module.scss";
import {
  addToCart,
  getCart,
  removeCartItem,
  updateCartItem,
} from "../../api/cart";
import { getProducts } from "../../api/products";
import { useDispatch } from "react-redux";
import { removeFromCart } from "../../store/reducers/cartReducer";

// ─── Static data ──────────────────────────────────────────────────────────────

const sidebarLinks = [
  { label: "Home", icon: "🏠" },
  { label: "Categories", icon: "⊞" },
  { label: "Electronics", icon: "📱" },
  { label: "Fashion", icon: "👕" },
  { label: "Accessories", icon: "🛍" },
  { label: "Deals", icon: "🏷" },
  { label: "New Arrivals", icon: "✨" },
  { label: "Best Sellers", icon: "🔥" },
  { label: "Support", icon: "🎧" },
];

const availableCoupons = [
  {
    code: "WELCOME10",
    label: "10% Off on your first order",
    min: "Min. order $100",
    color: "orange",
  },
  {
    code: "SAVE20",
    label: "20% Off on orders above $200",
    min: "Min. order $200",
    color: "blue",
  },
];

// ─── Normalizers ──────────────────────────────────────────────────────────────

// API cart item → UI shape
const toUIItem = (item) => ({
  id: item.product_id,
  name: item.product?.product_name ?? "Unknown Product",
  subtitle: item.product?.brand ?? "",
  price:
    item.product?.pricing?.sale_price ??
    item.product?.pricing?.regular_price ??
    0,
  img: item.product?.images?.[0]?.image_url ?? "",
  qty: item.quantity,
  inStock: item.product?.status === "active",
  color: "",
});

// API product → deal card shape
const toDeal = (p) => {
  const regular = p.pricing?.regular_price ?? 0;
  const sale = p.pricing?.sale_price ?? regular;
  const off =
    regular > 0 && regular !== sale
      ? Math.round(((regular - sale) / regular) * 100)
      : 0;
  return {
    id: p.product_id,
    name: p.product_name,
    sub: p.brand ?? "",
    price: sale,
    original: regular,
    off,
    img: p.images?.[0]?.image_url ?? "",
  };
};

// ─── Component ────────────────────────────────────────────────────────────────

export default function CartPage() {
  const history = useHistory();
  const dispatch = useDispatch();

  // Cart state
  const [items, setItems] = useState([]);
  const [cartLoading, setCartLoading] = useState(true);

  // Deals state
  const [deals, setDeals] = useState([]);
  const [dealsLoading, setDealsLoading] = useState(true);
  const [dealOffset, setDealOffset] = useState(0);

  // UI state
  const [selected, setSelected] = useState(() => new Set());
  const [coupon, setCoupon] = useState("");
  const [couponInput, setCouponInput] = useState("");
  const [couponApplied, setCouponApplied] = useState(false);

  // Per-item loading (prevents double-clicks mid-request)
  const [loadingIds, setLoadingIds] = useState(new Set());

  // ─── Data fetchers ─────────────────────────────────────────────

  const fetchCart = useCallback(async () => {
    try {
      const res = await getCart();
      const cartItems = res?.data?.cart?.items ?? [];
      //   console.log({ cartItems });
      setItems(cartItems.map(toUIItem));
    } catch (err) {
      console.error("Failed to load cart:", err);
    } finally {
      setCartLoading(false);
    }
  }, []);

  const fetchDeals = useCallback(async () => {
    try {
      const res = await getProducts(1, 12);
      // backend returns array directly
      const products = Array.isArray(res) ? res : (res?.data ?? []);
      setDeals(products.map(toDeal));
    } catch (err) {
      console.error("Failed to load deals:", err);
    } finally {
      setDealsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCart();
    fetchDeals();
  }, [fetchCart, fetchDeals]);

  // ─── Derived values ────────────────────────────────────────────

  const SHIPPING = items.length > 0 ? 9.99 : 0;
  const subtotal = items.reduce((s, i) => s + i.price * i.qty, 0);
  const DISCOUNT = couponApplied ? +(subtotal * 0.15).toFixed(2) : 0;
  const total = Math.max(0, subtotal - DISCOUNT + SHIPPING);
  const freeShippingProgress = Math.min((subtotal / 100) * 100, 100);
  const remaining = Math.max(100 - subtotal, 0);
  const allSelected =
    items.length > 0 && items.every((i) => selected.has(i.id));
  const visibleDeals = deals.slice(dealOffset, dealOffset + 3);

  // ─── Handlers ──────────────────────────────────────────────────

  const setItemLoading = (id, state) =>
    setLoadingIds((prev) => {
      const next = new Set(prev);
      state ? next.add(id) : next.delete(id);
      return next;
    });

  // Increment / decrement — if qty drops to 0 it removes the item
  const updateQty = async (id, delta) => {
    const item = items.find((i) => i.id === id);
    if (!item || loadingIds.has(id)) return;

    const newQty = item.qty + delta;

    if (newQty < 1) {
      removeItem(id);
      return;
    }

    // Optimistic UI update
    setItems((prev) =>
      prev.map((i) => (i.id === id ? { ...i, qty: newQty } : i)),
    );

    setItemLoading(id, true);
    try {
      await updateCartItem(id, { quantity: newQty });
    } catch (err) {
      console.error("Failed to update quantity:", err);
      // Revert on failure
      setItems((prev) =>
        prev.map((i) => (i.id === id ? { ...i, qty: item.qty } : i)),
      );
    } finally {
      setItemLoading(id, false);
    }
  };

  const removeItem = async (id) => {
    if (loadingIds.has(id)) return;

    // Optimistic removal
    const snapshot = [...items];
    setItems((prev) => prev.filter((i) => i.id !== id));
    setSelected((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });

    setItemLoading(id, true);
    try {
      await removeCartItem(id);
      dispatch(removeFromCart({ id }));
    } catch (err) {
      console.error("Failed to remove item:", err);
      setItems(snapshot); // revert
    } finally {
      setItemLoading(id, false);
    }
  };

  const removeSelected = async () => {
    if (selected.size === 0) return;
    const ids = Array.from(selected);

    // Optimistic removal
    const snapshot = [...items];
    setItems((prev) => prev.filter((i) => !ids.includes(i.id)));
    setSelected(new Set());

    try {
      await Promise.all(ids.map((id) => removeCartItem(id)));
    } catch (err) {
      console.error("Failed to remove selected items:", err);
      setItems(snapshot);
    }
  };

  const addDeal = async (deal) => {
    try {
      await addToCart({ product_id: deal.id, quantity: 1 });
      await fetchCart(); // re-fetch to get enriched cart with product details
    } catch (err) {
      console.error("Failed to add deal to cart:", err);
    }
  };

  const toggleSelect = (id) =>
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const toggleAll = () =>
    allSelected
      ? setSelected(new Set())
      : setSelected(new Set(items.map((i) => i.id)));

  const applyCode = (code) => {
    setCoupon(code);
    setCouponApplied(true);
  };

  const removeCoupon = () => {
    setCouponApplied(false);
    setCoupon("");
    setCouponInput("");
  };

  // ─── Loading state ─────────────────────────────────────────────

  if (cartLoading) {
    return (
      <div className={styles.page}>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh",
            fontSize: 18,
            color: "#888",
          }}
        >
          Loading your cart...
        </div>
      </div>
    );
  }

  // ─── Render ────────────────────────────────────────────────────

  return (
    <div className={styles.page}>
      {/* Sidebar */}
      <aside className={styles.sidebar}>
        <div className={styles.logo}>
          <span className={styles.hamburger}>☰</span>
          <span className={styles.logoText}>Ome</span>
        </div>
        <nav className={styles.nav}>
          {sidebarLinks.map((l) => (
            <a
              key={l.label}
              className={`${styles.navLink} ${
                l.label === "Electronics" ? styles.active : ""
              }`}
            >
              <span className={styles.navIcon}>{l.icon}</span>
              {l.label}
            </a>
          ))}
        </nav>
        <div className={styles.promoCard}>
          <p className={styles.promoTitle}>Get 15% Off</p>
          <p className={styles.promoSub}>On your first order</p>
          <button className={styles.shopNow}>Shop Now</button>
          <div className={styles.giftBox}>🎁</div>
        </div>
      </aside>

      {/* Main */}
      <main className={styles.main}>
        {/* Top bar */}
        <header className={styles.topbar}>
          <div className={styles.searchWrap}>
            <input
              className={styles.search}
              placeholder="Search for products, brands and more..."
            />
            <span className={styles.searchIcon}>🔍</span>
          </div>
          <div className={styles.topActions}>
            <button className={styles.topBtn}>👤 Account</button>
            <button className={styles.topBtn}>♡ Wishlist</button>
            <button className={styles.topBtn}>
              🛒 Cart
              {items.length > 0 && (
                <span className={styles.cartBadge}>
                  {items.reduce((sum, i) => sum + i.qty, 0)}
                </span>
              )}
            </button>
          </div>
        </header>

        <div className={styles.content}>
          <div className={styles.cartArea}>
            {/* Cart header */}
            <div className={styles.cartHeader}>
              <h1 className={styles.cartTitle}>
                Your Cart{" "}
                <span className={styles.cartCount}>({items.length})</span>
              </h1>
              <p className={styles.cartSub}>
                Review your items and proceed to checkout
              </p>
            </div>

            {/* Free shipping progress bar */}
            <div className={styles.shippingBanner}>
              <span className={styles.truckIcon}>🚚</span>
              <div className={styles.shippingInfo}>
                {remaining > 0 ? (
                  <>
                    <strong>You are eligible for FREE Shipping!</strong> Add{" "}
                    <strong>${remaining.toFixed(2)}</strong> more to get free
                    shipping.
                  </>
                ) : (
                  <strong>You've unlocked FREE Shipping! 🎉</strong>
                )}
              </div>
              <div className={styles.progressWrap}>
                <div className={styles.progressBar}>
                  <div
                    className={styles.progressFill}
                    style={{ width: `${freeShippingProgress}%` }}
                  />
                </div>
                <span className={styles.progressLabel}>
                  ${subtotal.toFixed(2)} / $100
                </span>
              </div>
            </div>

            {/* Cart items */}
            {items.length === 0 ? (
              <div className={styles.itemsList}>
                <div
                  className={styles.cartItem}
                  style={{
                    flexDirection: "column",
                    textAlign: "center",
                    gap: 12,
                  }}
                >
                  <p className={styles.itemName}>Your cart is empty.</p>
                  <p className={styles.itemSub}>
                    Browse the catalog and add products to get started.
                  </p>
                  <button
                    className={styles.checkoutBtn}
                    onClick={() => history.push("/category")}
                  >
                    Browse Products
                  </button>
                </div>
              </div>
            ) : (
              <div className={styles.itemsList}>
                {items.map((item) => (
                  <div
                    key={item.id}
                    className={styles.cartItem}
                    style={{ opacity: loadingIds.has(item.id) ? 0.5 : 1 }}
                  >
                    <input
                      type="checkbox"
                      checked={selected.has(item.id)}
                      onChange={() => toggleSelect(item.id)}
                      className={styles.checkbox}
                    />
                    <img
                      src={item.img}
                      alt={item.name}
                      className={styles.itemImg}
                      onError={(e) => {
                        e.target.src =
                          "https://via.placeholder.com/80x80?text=No+Image";
                      }}
                    />
                    <div className={styles.itemInfo}>
                      <p className={styles.itemName}>{item.name}</p>
                      <p className={styles.itemSub}>{item.subtitle}</p>
                      {item.color && (
                        <p className={styles.itemColor}>Color: {item.color}</p>
                      )}
                      <p className={styles.itemPrice}>
                        ${item.price.toFixed(2)}{" "}
                        <span className={styles.inStock}>
                          {item.inStock ? "In Stock" : "Out of Stock"}
                        </span>
                      </p>
                    </div>
                    <div className={styles.qtyControl}>
                      <button
                        onClick={() => updateQty(item.id, -1)}
                        className={styles.qtyBtn}
                        disabled={loadingIds.has(item.id)}
                      >
                        −
                      </button>
                      <span className={styles.qtyVal}>{item.qty}</span>
                      <button
                        onClick={() => updateQty(item.id, +1)}
                        className={styles.qtyBtn}
                        disabled={loadingIds.has(item.id)}
                      >
                        +
                      </button>
                    </div>
                    <p className={styles.linePrice}>
                      ${(item.price * item.qty).toFixed(2)}
                    </p>
                    <button
                      onClick={() => removeItem(item.id)}
                      className={styles.removeBtn}
                      disabled={loadingIds.has(item.id)}
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Bulk actions */}
            {items.length > 0 && (
              <div className={styles.bulkActions}>
                <label className={styles.selectAll}>
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={toggleAll}
                    className={styles.checkbox}
                  />
                  Select All
                </label>
                <div className={styles.bulkRight}>
                  <button className={styles.bulkBtn} onClick={removeSelected}>
                    🗑 Remove Selected
                  </button>
                  <button className={styles.bulkBtn}>♡ Move to Wishlist</button>
                </div>
              </div>
            )}

            {/* Top Deals — fetched from products API */}
            <div className={styles.dealsSection}>
              <div className={styles.dealsHeader}>
                <h2 className={styles.dealsTitle}>Top Deals For You</h2>
                <div className={styles.dealTimer}>
                  Offer ends in
                  <span className={styles.timerBlock}>08</span>:
                  <span className={styles.timerBlock}>45</span>:
                  <span className={styles.timerBlock}>32</span>
                  <span className={styles.timerLabels}>
                    <em>Hrs</em>
                    <em>Mins</em>
                    <em>Secs</em>
                  </span>
                </div>
              </div>

              {dealsLoading ? (
                <p style={{ padding: "16px", color: "#888" }}>
                  Loading deals...
                </p>
              ) : deals.length === 0 ? (
                <p style={{ padding: "16px", color: "#888" }}>
                  No deals available right now.
                </p>
              ) : (
                <div className={styles.dealsCarousel}>
                  <button
                    className={styles.carouselBtn}
                    onClick={() => setDealOffset(Math.max(0, dealOffset - 1))}
                    disabled={dealOffset === 0}
                  >
                    ‹
                  </button>
                  <div className={styles.dealsGrid}>
                    {visibleDeals.map((d) => (
                      <div key={d.id} className={styles.dealCard}>
                        <img
                          src={d.img}
                          alt={d.name}
                          className={styles.dealImg}
                          onError={(e) => {
                            e.target.src =
                              "https://via.placeholder.com/80x80?text=No+Image";
                          }}
                        />
                        <div className={styles.dealInfo}>
                          <p className={styles.dealName}>{d.name}</p>
                          <p className={styles.dealSub}>{d.sub}</p>
                          <div className={styles.dealPricing}>
                            <span className={styles.dealPrice}>
                              ${d.price.toFixed(2)}
                            </span>
                            {d.original !== d.price && (
                              <span className={styles.dealOriginal}>
                                ${d.original.toFixed(2)}
                              </span>
                            )}
                          </div>
                          {d.off > 0 && (
                            <span className={styles.dealOff}>{d.off}% OFF</span>
                          )}
                        </div>
                        <button
                          className={styles.addBtn}
                          onClick={() => addDeal(d)}
                        >
                          + Add
                        </button>
                      </div>
                    ))}
                  </div>
                  <button
                    className={styles.carouselBtn}
                    onClick={() =>
                      setDealOffset(Math.min(deals.length - 3, dealOffset + 1))
                    }
                    disabled={dealOffset >= deals.length - 3}
                  >
                    ›
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Order Summary Sidebar */}
          <aside className={styles.orderSidebar}>
            {/* Coupon input */}
            <div className={styles.couponBox}>
              <h3 className={styles.couponTitle}>Coupon Code</h3>
              <div className={styles.couponRow}>
                <input
                  className={styles.couponInput}
                  placeholder="Enter coupon code"
                  value={couponInput}
                  onChange={(e) => setCouponInput(e.target.value)}
                />
                <button
                  className={styles.applyBtn}
                  onClick={() => couponInput && applyCode(couponInput)}
                >
                  Apply
                </button>
              </div>
              {couponApplied && (
                <div className={styles.appliedTag}>
                  <span className={styles.appliedIcon}>✅</span>
                  <div>
                    <p className={styles.appliedCode}>{coupon} Applied!</p>
                    <p className={styles.appliedSave}>
                      You saved ${DISCOUNT.toFixed(2)}
                    </p>
                  </div>
                  <button className={styles.removeTag} onClick={removeCoupon}>
                    ✕
                  </button>
                </div>
              )}
            </div>

            {/* Available coupons */}
            <div className={styles.availCoupons}>
              <div className={styles.availHeader}>
                <span>Available Coupons</span>
                <a className={styles.viewAll}>View All</a>
              </div>
              {availableCoupons.map((c) => (
                <div
                  key={c.code}
                  className={`${styles.couponCard} ${styles[c.color]}`}
                >
                  <div className={styles.couponCardInfo}>
                    <p className={styles.couponCode}>{c.code}</p>
                    <p className={styles.couponLabel}>{c.label}</p>
                    <p className={styles.couponMin}>{c.min}</p>
                  </div>
                  <button
                    className={styles.useBtn}
                    onClick={() => applyCode(c.code)}
                  >
                    Use
                  </button>
                </div>
              ))}
            </div>

            {/* Order summary */}
            <div className={styles.summaryBox}>
              <h3 className={styles.summaryTitle}>Order Summary</h3>
              <div className={styles.summaryRow}>
                <span>Subtotal ({items.length} items)</span>
                <span>${subtotal.toFixed(2)}</span>
              </div>
              {couponApplied && (
                <div className={`${styles.summaryRow} ${styles.discount}`}>
                  <span>Discount ({coupon})</span>
                  <span>- ${DISCOUNT.toFixed(2)}</span>
                </div>
              )}
              <div className={styles.summaryRow}>
                <span>Shipping ⓘ</span>
                <span>${SHIPPING.toFixed(2)}</span>
              </div>
              <div className={`${styles.summaryRow} ${styles.totalRow}`}>
                <strong>Total</strong>
                <strong className={styles.totalAmt}>${total.toFixed(2)}</strong>
              </div>
              {couponApplied && (
                <p className={styles.savedMsg}>
                  You saved ${DISCOUNT.toFixed(2)}
                </p>
              )}
              <div className={styles.freeShipNote}>
                🚚 Free Shipping on orders above $100
              </div>
              <button
                className={styles.checkoutBtn}
                disabled={items.length === 0}
              >
                🔒 Proceed to Checkout
              </button>
              <button
                className={styles.continueBtn}
                onClick={() => history.push("/category")}
              >
                ← Continue Shopping
              </button>
            </div>
          </aside>
        </div>

        {/* Footer trust badges */}
        <footer className={styles.trustBar}>
          {[
            { icon: "🚚", title: "Free Shipping", sub: "On orders over $100" },
            { icon: "🔄", title: "30-Day Returns", sub: "Hassle free returns" },
            { icon: "🔒", title: "Secure Payment", sub: "100% protected" },
            { icon: "🎧", title: "24/7 Support", sub: "We're here to help" },
          ].map((b) => (
            <div key={b.title} className={styles.trustBadge}>
              <span className={styles.badgeIcon}>{b.icon}</span>
              <div>
                <p className={styles.badgeTitle}>{b.title}</p>
                <p className={styles.badgeSub}>{b.sub}</p>
              </div>
            </div>
          ))}
        </footer>
      </main>
    </div>
  );
}
